import { router } from "@inertiajs/vue3";
import route from "ziggy-js";
import {
    StoreDayOffRequestSchema,
    UpdateDayOffRequestSchema,
    type StoreDayOffRequest,
    type UpdateDayOffRequest,
} from "@/types/DaysOff";
import type { z } from "zod";

type ValidationResult<T> =
    | { success: true; data: T }
    | { success: false; errors: z.ZodError };

type RequestOptions = {
    onSuccess?: () => void;
    onError?: (errors: Record<string, string>) => void;
    onFinish?: () => void;
};

export function useDaysOff() {
    const createDayOff = (
        payload: StoreDayOffRequest,
        options?: RequestOptions,
    ): ValidationResult<StoreDayOffRequest> => {
        const result = StoreDayOffRequestSchema.safeParse(payload);

        if (!result.success) {
            return { success: false, errors: result.error };
        }

        router.post(route("app.days_off.store"), result.data, {
            onSuccess: () => options?.onSuccess?.(),
            onError: (errors) =>
                options?.onError?.(errors as Record<string, string>),
            onFinish: () => options?.onFinish?.(),
        });

        return { success: true, data: result.data };
    };

    const updateDayOff = (
        id: number,
        payload: UpdateDayOffRequest,
        options?: RequestOptions,
    ): ValidationResult<UpdateDayOffRequest> => {
        const result = UpdateDayOffRequestSchema.safeParse(payload);

        if (!result.success) {
            return { success: false, errors: result.error };
        }

        router.put(route("app.days_off.update", { dayOff: id }), result.data, {
            onSuccess: () => options?.onSuccess?.(),
            onError: (errors) =>
                options?.onError?.(errors as Record<string, string>),
            onFinish: () => options?.onFinish?.(),
        });

        return { success: true, data: result.data };
    };

    const deleteDayOff = (id: number, options?: RequestOptions): void => {
        router.delete(route("app.days_off.delete", { dayOff: id }), {
            onSuccess: () => options?.onSuccess?.(),
            onError: (errors) =>
                options?.onError?.(errors as Record<string, string>),
            onFinish: () => options?.onFinish?.(),
        });
    };

    return {
        createDayOff,
        updateDayOff,
        deleteDayOff,
    };
}
